import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/common';
import { useScreeningList, errorMessage } from './api';
import { statuses, holdReasons, label, type MonitoringFilters } from './types';
import { fieldClass, ScreeningBadge, ScreeningNotice, time } from './shared';

export function readFilters(params: URLSearchParams): MonitoringFilters {
  const positive = (key: string, fallback: number, max: number) => {
    const raw = params.get(key) ?? '';
    const value = /^\d+$/.test(raw) ? Number(raw) : fallback;
    return Number.isSafeInteger(value) && value >= 1 && value <= max ? value : fallback;
  };
  const status = params.get('status');
  const type = params.get('type');
  const reason = params.get('hold_reason');
  const date = (key: string) => {
    const raw = params.get(key);
    return raw &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw) &&
      !Number.isNaN(Date.parse(raw + 'Z'))
      ? raw
      : undefined;
  };
  return {
    status: statuses.find((s) => s === status),
    type: type === 'outbound' || type === 'incoming' ? type : undefined,
    hold_reason: holdReasons.find((r) => r === reason),
    from: date('from'),
    to: date('to'),
    page: positive('page', 1, Number.MAX_SAFE_INTEGER),
    limit: positive('limit', 20, 100),
  };
}
export function TransactionMonitoringPage() {
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const invalidRange = !!(filters.from && filters.to && filters.from > filters.to);
  const query = useScreeningList({
    ...filters,
    from: filters.from ? filters.from + ':00Z' : undefined,
    to: filters.to ? filters.to + ':00Z' : undefined,
  });
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.set('page', '1');
    setParams(next);
  };
  const select = (title: string, key: string, choices: readonly string[], value: string = '') => (
    <label className="text-xs text-[#666]">
      {title}
      <select
        className={fieldClass + ' mt-1'}
        value={value}
        onChange={(e) => update(key, e.target.value)}
      >
        <option value="">All</option>
        {choices.map((c) => (
          <option key={c} value={c}>
            {c === 'outbound' ? 'Outgoing' : label(c)}
          </option>
        ))}
      </select>
    </label>
  );
  const data = query.data;
  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-[26px] font-extrabold text-[#1a1a1a]">Compliance Panel</h1>
        <p className="mt-1 text-sm text-[#777]">
          Inspect payment screening, evidence and decision history for the active tenant.
        </p>
      </header>
      <ScreeningNotice />
      <div className="grid gap-3 rounded-lg border border-[#e8e8e8] bg-white p-4 sm:grid-cols-2 xl:grid-cols-3">
        {select('Status', 'status', statuses, filters.status)}
        {select('Type', 'type', ['outbound', 'incoming'], filters.type)}
        {select('Hold reason', 'hold_reason', holdReasons, filters.hold_reason)}
        {(['from', 'to'] as const).map((key) => (
          <label key={key} className="min-w-0 text-xs text-[#666]">
            Requested {key} (UTC)
            <input
              className={fieldClass + ' mt-1 min-w-0'}
              type="datetime-local"
              value={filters[key] ?? ''}
              onChange={(e) => update(key, e.target.value)}
            />
          </label>
        ))}
        <label className="text-xs text-[#666]">
          Page size (maximum 100)
          <select
            className={fieldClass + ' mt-1'}
            value={filters.limit}
            onChange={(e) => update('limit', e.target.value)}
          >
            {[...new Set([10, 20, 50, 100, filters.limit])]
              .sort((a, b) => a - b)
              .map((n) => (
                <option key={n}>{n}</option>
              ))}
          </select>
        </label>
        <Button variant="ghost" size="sm" onClick={() => setParams({})}>
          Clear filters
        </Button>
      </div>
      {invalidRange && <p role="alert">Requested from must precede requested to.</p>}
      {query.isPending && <p role="status">Loading screenings…</p>}
      {query.isError && (
        <div role="alert" className="rounded-lg bg-[#ffebee] p-4 text-sm text-[#c62828]">
          {errorMessage(query.error)}{' '}
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </div>
      )}
      {data && !query.isError && (
        <div className="overflow-hidden rounded-lg border border-[#e8e8e8] bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">Tenant payment screenings</caption>
              <thead className="bg-[#fafafa] text-xs text-[#777]">
                <tr>
                  {[
                    'Transaction reference',
                    'Type',
                    'Client ID',
                    'Screening status',
                    'Hold reason',
                    'Requested time',
                    'Last successful sync',
                    'Action',
                  ].map((h) => (
                    <th key={h} className="whitespace-nowrap px-4 py-3 font-semibold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr key={row.screening_id} className="border-t border-[#eee] hover:bg-[#fafafa]">
                    <td className="px-4 py-4 font-medium">{row.transaction_id}</td>
                    <td className="px-4 py-4">
                      {row.transaction_type === 'outbound' ? 'Outgoing' : 'Incoming'}
                    </td>
                    <td className="px-4 py-4">{row.client_id}</td>
                    <td className="px-4 py-4">
                      <ScreeningBadge status={row.status} />
                    </td>
                    <td className="px-4 py-4">
                      {row.hold_reason ? label(row.hold_reason) : 'None'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">{time(row.requested_at)}</td>
                    <td className="px-4 py-4 whitespace-nowrap">{time(row.last_synced_at)}</td>
                    <td className="px-4 py-4">
                      <Link
                        className="font-semibold text-[#6B0F1A] underline"
                        aria-label={`View ${row.transaction_id}`}
                        to={`/transaction-monitoring/${row.screening_id}`}
                      >
                        View details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.items.length === 0 && (
            <p className="p-8 text-center text-sm text-[#777]">
              No screenings match these filters.
            </p>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#eee] p-4 text-sm">
            <p>
              {data.total} screenings · Page {data.page} of{' '}
              {Math.max(1, Math.ceil(data.total / data.limit))}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={filters.page <= 1 || query.isFetching}
                onClick={() => update('page', String(filters.page - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={filters.page * filters.limit >= data.total || query.isFetching}
                onClick={() => update('page', String(filters.page + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
